import { useCookieConsent } from '@/contexts/CookieContext';
import { trackPageView } from '@/utils/analytics';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to automatically track page views in Firebase Analytics
 * Respects user's cookie consent preferences and prevents duplicate tracking
 *
 * @returns {void}
 */
export const useFirebaseAnalytics = () => {
  const location = useLocation();
  const { consent } = useCookieConsent();

  // Track the last page to prevent duplicate tracking
  const lastTrackedPage = useRef(null);

  // Track if we've already initialized analytics in this session
  const analyticsInitialized = useRef(false);

  useEffect(() => {
    // Only proceed if analytics consent is given
    if (!consent?.analytics) {
      return;
    }

    // Create a unique page identifier
    const currentPage = location.pathname + location.search;

    // Prevent duplicate tracking of the same page
    if (lastTrackedPage.current === currentPage) {
      return;
    }

    try {
      // Track the page view
      trackPageView(currentPage);

      // Update the last tracked page
      lastTrackedPage.current = currentPage;

      // Mark analytics as initialized after first successful track
      if (!analyticsInitialized.current) {
        analyticsInitialized.current = true;
      }
    } catch (error) {
      // Only log errors in development
      if (import.meta.env.MODE === 'development') {
        console.error('Error tracking page view:', error);
      }
    }
  }, [location.pathname, location.search, consent?.analytics]);

  // Cleanup function
  useEffect(() => {
    return () => {
      // Reset tracking state if component unmounts
      lastTrackedPage.current = null;
    };
  }, []);
};

/**
 * Hook to track page views manually
 * Useful for tracking virtual page views, custom navigation events, or modal views
 *
 * @returns {Object} Object with trackPage function
 * @returns {Function} returns.trackPage - Function to manually track a page view
 * @example
 * function ProductModal({ productId }) {
 *   const { trackPage } = useFirebasePageTracking();
 *
 *   useEffect(() => {
 *     // Track modal view as a virtual page
 *     trackPage(`/modal/product/${productId}`, `Product ${productId}`);
 *   }, [productId, trackPage]);
 *
 *   return <div>Product Modal</div>;
 * }
 */
export const useFirebasePageTracking = () => {
  const { consent } = useCookieConsent();

  // Use ref to store last tracked pages to prevent duplicates
  const trackedPages = useRef(new Set());

  /**
   * Track a page view manually
   *
   * @param {string} pagePath - Page path to track (e.g., '/products/123')
   * @param {string} [pageTitle] - Optional page title
   * @param {Object} [options] - Additional options
   * @param {boolean} [options.allowDuplicate=false] - Allow tracking the same page multiple times
   * @returns {void}
   * @example
   * trackPage('/products/123', 'Product Details');
   * trackPage('/modal/checkout', 'Checkout Modal', { allowDuplicate: true });
   */
  const trackPage = (pagePath, pageTitle, options = {}) => {
    const { allowDuplicate = false } = options;

    // Only track if analytics consent is given
    if (!consent?.analytics) {
      if (import.meta.env.MODE === 'development') {
        console.info('Analytics tracking skipped: No consent');
      }
      return;
    }

    // Prevent duplicate tracking unless explicitly allowed
    if (!allowDuplicate && trackedPages.current.has(pagePath)) {
      if (import.meta.env.MODE === 'development') {
        console.info(`Page already tracked: ${pagePath}`);
      }
      return;
    }

    try {
      trackPageView(pagePath, pageTitle);

      // Add to tracked pages set
      if (!allowDuplicate) {
        trackedPages.current.add(pagePath);

        // Limit the set size to prevent memory leaks
        if (trackedPages.current.size > 50) {
          // Remove the oldest entry
          const firstItem = trackedPages.current.values().next().value;
          trackedPages.current.delete(firstItem);
        }
      }
    } catch (error) {
      // Only log errors in development
      if (import.meta.env.MODE === 'development') {
        console.error('Error tracking manual page view:', error);
      }
    }
  };

  return { trackPage };
};
